export type Relationships<Ref> = [Ref, Ref | null | undefined][];

/** A lookup from a node to its direct parent (or `null`/`undefined` for roots). */
export type ParentMap<Ref> = ReadonlyMap<Ref, Ref | null | undefined>;

export function getRootParents<T>(
  relationships: Relationships<T>,
): Relationships<T> {
  return relationships.filter((r) => !r[1]);
}

export function getChildren<T>(
  relationships: Relationships<T>,
  parent: T,
): Relationships<T> {
  return relationships.filter((r) => r[1] === parent);
}

export function getParent<T>(
  relationships: Relationships<T>,
  child: T,
): T | null {
  return relationships.find((r) => r[0] === child)?.[1] ?? null;
}
/**
 * Builds a child -> parent lookup from the flat relationship tuples.
 *
 * Constructing this once and reusing it makes repeated ancestor lookups O(depth)
 * instead of O(relationships) per call.
 */
export function toParentMap<Ref>(
  relationships: Relationships<Ref>,
): ParentMap<Ref> {
  const parentMap = new Map<Ref, Ref | null | undefined>();
  for (const [child, parent] of relationships) {
    parentMap.set(child, parent);
  }
  return parentMap;
}

/**
 * Returns the ancestor chain of `ref` ordered from the root down to its
 * immediate parent (excluding `ref` itself). Guards against cycles.
 */
export function getAncestors<Ref>(parentMap: ParentMap<Ref>, ref: Ref): Ref[] {
  const ancestors: Ref[] = [];
  const seen = new Set<Ref>();

  let current = parentMap.get(ref) ?? null;
  while (current !== null && !seen.has(current)) {
    seen.add(current);
    ancestors.push(current);
    current = parentMap.get(current) ?? null;
  }

  return ancestors.reverse();
}
